'use client';

import { useQueryClient } from '@tanstack/react-query';

import { useInvestorQuery } from '@/hooks/use-investor-query';
import { fetchInvestorPerformance } from '@/lib/investor-analytics';
import { portfolioKeys } from '@/lib/investor-keys';
import { SLOW_CADENCE_MS } from '../api/keys';
import type {
  InvestorPerformance,
  InvestorPerformanceQuery
} from '@/lib/investor-analytics';

export function usePerformanceChart(
  params: InvestorPerformanceQuery = {},
  options: { enabled?: boolean } = {}
) {
  const queryClient = useQueryClient();

  return useInvestorQuery<InvestorPerformance>({
    key: ({ address, chainId }) =>
      portfolioKeys.performance(address, chainId, params),
    fetch: ({ address, chainId }, signal) => {
      // The last response for this exact query (same wallet, chain and
      // window) says where the window really starts. A wallet younger than
      // its window is retried from its first deposit once; every poll after
      // reads that start from here and asks once.
      const previous = queryClient.getQueryData<InvestorPerformance>(
        portfolioKeys.performance(address, chainId, params)
      );
      return fetchInvestorPerformance(address, chainId, params, signal, {
        resolvedFrom: previous?.from
      });
    },
    enabled: options.enabled !== false,
    cadenceMs: SLOW_CADENCE_MS,
    // The series is cached for 300s server-side, but it goes stale for the
    // reader sooner than the poll refreshes it.
    staleTime: 2 * 60 * 1000
  });
}
